import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    Tooltip,
    YAxis,
    XAxis,
} from 'recharts';

const formatNumberShort = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '0';
    const num = Math.abs(Number(value));
    const sign = Number(value) < 0 ? '-' : '';
    if (num >= 1_000_000) return `${sign}${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${sign}${(num / 1_000).toFixed(1)}k`;
    return `${sign}${num.toFixed(0)}`;
};

const formatNumberFull = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
};

const formatDateShort = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const SparklineTooltip = ({ active, payload, unit }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
        <div
            style={{
                background: '#0f172a',
                color: '#f8fafc',
                borderRadius: '0.375rem',
                padding: '0.35rem 0.55rem',
                fontSize: '0.7rem',
                lineHeight: 1.3,
                boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
            }}
        >
            <div style={{ opacity: 0.7 }}>{formatDateShort(point.date)}</div>
            <div style={{ fontWeight: 600 }}>
                {formatNumberFull(point.balance)} {unit || ''}
            </div>
        </div>
    );
};

// Each instance gets its own gradient id so two sparklines on one page don't
// collide. The counter is module-level (cheap, monotonic, unique per render
// session); we don't need cross-session stability because the id is only
// referenced by the same component's <defs> block.
let sparklineGradientCounter = 0;

const StockpileSparkline = ({ series, unit, height = 80 }) => {
    const gradientId = useMemo(
        () => `stockpileSparkFill-${++sparklineGradientCounter}`,
        []
    );

    const data = useMemo(
        () =>
            (series || []).map((point) => ({
                date: point.date,
                balance: Number(point.balance),
            })),
        [series]
    );

    if (!data.length) {
        return (
            <div className="stockpile-sparkline empty">
                <span>No 30-day history yet.</span>
            </div>
        );
    }

    const values = data.map((d) => d.balance);
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Pad the domain a little so the line doesn't kiss the chart edges.
    const yDomain = min === max
        ? [min - Math.max(1, Math.abs(min) * 0.05), max + Math.max(1, Math.abs(max) * 0.05)]
        : [min - (max - min) * 0.1, max + (max - min) * 0.1];

    // Two ticks: highest at top, lowest at bottom — Recharts orients the
    // Y axis high-to-low top-to-bottom by default.
    const yTicks = [yDomain[0], yDomain[1]];

    return (
        <div className="stockpile-sparkline" style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <YAxis
                        domain={yDomain}
                        ticks={yTicks}
                        tickFormatter={formatNumberShort}
                        tick={{ fontSize: 9, fill: '#64748b' }}
                        width={36}
                        axisLine={false}
                        tickLine={false}
                    />
                    <XAxis dataKey="date" hide />
                    <Tooltip
                        cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                        content={<SparklineTooltip unit={unit} />}
                    />
                    <Area
                        type="monotone"
                        dataKey="balance"
                        stroke="#1d4ed8"
                        strokeWidth={1.5}
                        fill={`url(#${gradientId})`}
                        isAnimationActive={false}
                        dot={false}
                        activeDot={{ r: 3, fill: '#1d4ed8' }}
                    />
                </AreaChart>
            </ResponsiveContainer>
            <div className="stockpile-sparkline-axis">
                <span>{formatDateShort(data[0].date)}</span>
                <span>{formatDateShort(data[data.length - 1].date)}</span>
            </div>
        </div>
    );
};

export default StockpileSparkline;
